import React, { useEffect, useState } from "react";
import { X, Money, Receipt } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { generatePaymentReceiptPDF } from "../lib/paymentReceipt";

/**
 * Modal para que el admin registre un pago en efectivo recibido en mano
 * y genere automáticamente el comprobante PDF. Queda en estado "registrado"
 * (cuenta como efectivo en el dashboard).
 */
export default function CashPaymentModal({ open, onClose, onCreated }) {
  const [athletes, setAthletes] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const monthLabel = new Date().toLocaleString("es-AR", { month: "long", year: "numeric" });

  const [form, setForm] = useState({
    user_id: "",
    amount: "",
    concept: `Mensualidad ${monthLabel}`,
    reference: "",
    payment_date: today,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoadingUsers(true);
    api.get("/users")
      .then(({ data }) => {
        const list = (data || [])
          .filter((u) => u.role !== "admin")
          .sort((a, b) => (a.name || "").localeCompare(b.name || "", "es"));
        setAthletes(list);
      })
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoadingUsers(false));
  }, [open]);

  // Auto-completar monto cuando se selecciona atleta con cuota configurada
  const selectAthlete = (uid) => {
    const u = athletes.find((a) => a.user_id === uid);
    const fee = u?.profile?.monthly_fee;
    setForm((f) => ({
      ...f,
      user_id: uid,
      amount: fee ? String(fee) : f.amount,
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.user_id) {
      toast.error("Seleccioná un atleta");
      return;
    }
    const amount = Number(form.amount);
    if (!amount || amount <= 0) {
      toast.error("Ingresá un monto válido");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/users/${form.user_id}/payments`, {
        amount,
        method: "efectivo",
        concept: form.concept || "Mensualidad Athlon Sports",
        reference: form.reference || null,
        payment_date: form.payment_date,
      });
      toast.success(`Recibo ${data.receipt_number} generado · Pagado mensualidad`);
      const target = athletes.find((a) => a.user_id === form.user_id);
      try {
        await generatePaymentReceiptPDF(data, {
          user_id: form.user_id,
          name: target?.name,
          email: target?.email,
        });
      } catch (err) { console.error(err); }
      onCreated?.();
      onClose();
      setForm({ user_id: "", amount: "", concept: `Mensualidad ${monthLabel}`, reference: "", payment_date: today });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="cash-payment-modal">
      <div className="card-base p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center gap-2 mb-5">
          <Money size={22} weight="duotone" className="text-[#FFD60A]" />
          <h2 className="font-display font-black uppercase text-xl tracking-tighter flex-1">Registrar pago en efectivo</h2>
          <button onClick={onClose} className="text-muted hover:text-white" data-testid="cash-modal-close">
            <X size={20} weight="bold" />
          </button>
        </div>

        <p className="text-xs text-muted mb-4">
          Generá el comprobante de un pago recibido en mano. Queda registrado como
          &quot;Pagado mensualidad&quot; en el dashboard y se descarga el PDF automáticamente.
        </p>

        <form onSubmit={submit} className="space-y-4" data-testid="cash-form">
          <div>
            <label className="text-xs uppercase tracking-widest text-muted block mb-2">Atleta</label>
            <select
              className="input-base"
              value={form.user_id}
              onChange={(e) => selectAthlete(e.target.value)}
              required
              data-testid="cash-athlete-select"
            >
              <option value="">{loadingUsers ? "Cargando..." : "Seleccionar atleta"}</option>
              {athletes.map((a) => (
                <option key={a.user_id} value={a.user_id}>
                  {a.name} {a.profile?.monthly_fee ? `· $${a.profile.monthly_fee.toLocaleString("es-AR")}` : ""}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-widest text-muted block mb-2">Monto (ARS)</label>
              <input
                type="number" min="1" step="0.01" className="input-base"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                required placeholder="15000"
                data-testid="cash-amount"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-muted block mb-2">Fecha</label>
              <input
                type="date" className="input-base"
                value={form.payment_date}
                onChange={(e) => setForm({ ...form, payment_date: e.target.value })}
                data-testid="cash-date"
              />
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-muted block mb-2">Concepto</label>
            <input
              className="input-base"
              value={form.concept}
              onChange={(e) => setForm({ ...form, concept: e.target.value })}
              placeholder="Mensualidad Abril 2026"
              data-testid="cash-concept"
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-muted block mb-2">
              Observaciones (opcional)
            </label>
            <input
              className="input-base"
              value={form.reference}
              onChange={(e) => setForm({ ...form, reference: e.target.value })}
              placeholder="Ej: Entregado en mano · Pago parcial · etc."
              data-testid="cash-reference"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={submitting}
              className="btn-primary flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed flex-1"
              data-testid="cash-submit"
            >
              <Receipt size={16} weight="bold" />
              {submitting ? "Generando..." : "Generar comprobante"}
            </button>
            <button type="button" onClick={onClose} className="btn-secondary">Cancelar</button>
          </div>
        </form>
      </div>
    </div>
  );
}
